import { ComponentType, FocusEvent, FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../../utils/useTranslation';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import { InputTextButton } from './InputTextButton/InputTextButton';
import { Suggest } from './Suggest/Suggest';
import { formatTextDefault } from './Suggest/utils';
import { ControlProps } from './makeTextControl';

interface ControlPropsWithSuggest<TElement> extends ControlProps<TElement> {
    selectedItems: Record<string, string>;
    suggest: Record<string, string>;
    empty?: string;
    withFind?: boolean;
    onSelectItem: (key: string) => void;
    onRemoveItem: (key: string) => void;
    onInputSuggest?: (event: FormEvent<TElement>) => void;
    formatSuggestText?: (key: string, value: string) => string;
    formatSelectedText?: (key: string, value: string) => string;
    required?: boolean;
    errorMessage?: string;
}

export function makeTextSuggest<TElement extends HTMLInputElement>(
    Component: ComponentType<ControlProps<TElement>>,
) {
    return (props: ControlPropsWithSuggest<TElement>) => {
        const {
            suggest,
            selectedItems,
            empty,
            withFind = true,
            width = 150,
            height,
            required = false,
            onSelectItem,
            onRemoveItem,
            onInputSuggest,
            formatSuggestText,
            formatSelectedText = formatTextDefault,
            errorMessage,
        } = props;

        const t = useTranslation();

        const [init, setInit] = useState(false);
        const [error, setError] = useState(Boolean(errorMessage));

        const selectedIds = Object.keys(selectedItems);

        const [foundItems, setFindItems] = useState({});
        const [visibleSuggest, setVisibleSuggest] = useState(false);

        const onClose = useCallback(() => {
            setVisibleSuggest(false);
            setInit(true);
        }, []);

        const onFocus = useCallback(
            (event: FocusEvent<TElement>) => {
                const value = event.currentTarget.value;

                if (!value) {
                    setFindItems(suggest);
                }

                setVisibleSuggest(true);
                props.onFocus?.(event);
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [props.onFocus, suggest],
        );
        const onBlur = useCallback(
            (event: FocusEvent<TElement>) => {
                setTimeout(() => onClose(), 200);

                props.onBlur?.(event);
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [props.onBlur, onClose],
        );

        const findItems = useCallback(
            (event: FormEvent<TElement>) => {
                const value = event.currentTarget.value;
                const itemsKeys = Object.keys(suggest);
                const foundKeys = itemsKeys.filter((item) => item.includes(value));
                const newFoundItems = foundKeys.reduce(
                    (items, item) => ({ ...items, [item]: suggest[item] }),
                    {},
                );

                setFindItems(newFoundItems);
            },
            [suggest],
        );

        const onInput = useCallback(
            (event: FormEvent<TElement>) => {
                if (withFind) {
                    findItems(event);

                    return;
                }

                onInputSuggest?.(event);
            },
            [withFind, findItems, onInputSuggest],
        );

        const onRemoveItemClick = useCallback(
            (key: string) => {
                onRemoveItem(key);
                setInit(true);
            },
            [onRemoveItem],
        );

        useEffect(() => {
            setError(init && required && !selectedIds.length);
        }, [init, required, selectedIds.length]);

        return (
            <Box position="relative">
                <Component
                    {...props}
                    flexwrap="wrap"
                    width={selectedIds.length ? width : '100%'}
                    height={height}
                    padding="8px 16px 0"
                    mb={1}
                    minHeight={56}
                    alignItems="center"
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onInput={onInput}
                    startAdornment={
                        <Flex direction="row" flexWrap="wrap">
                            {selectedIds.map((item) => (
                                <InputTextButton
                                    key={item}
                                    mb={1}
                                    text={formatSelectedText(item, selectedItems[item])}
                                    value={item}
                                    onClick={onRemoveItemClick}
                                />
                            ))}
                        </Flex>
                    }
                    error={error}
                    required={false}
                />
                {error || errorMessage ? (
                    <Typography variant="caption" color="error" mt={1}>
                        {errorMessage || t('Поле обязательное')}
                    </Typography>
                ) : null}
                <Suggest
                    visible={visibleSuggest}
                    items={withFind ? foundItems : suggest}
                    onClick={onSelectItem}
                    empty={empty}
                    trigger={<Box />}
                    onClose={onClose}
                    formatText={formatSuggestText}
                />
            </Box>
        );
    };
}
